import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

type Store = Record<string, number>; // klucz: pathname+search
const STORAGE_KEY = "scroll-positions-v4";

const readStore = (): Store => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {};
  }
};
const writeStore = (s: Store) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    return;
  }
};

export default function ScrollRestorer() {
  const location = useLocation();
  const navType = useNavigationType(); // "PUSH" | "POP" | "REPLACE"
  const positionsRef = useRef<Store>(readStore());

  // wyłącz natywny mechanizm przeglądarki
  useEffect(() => {
    if ("scrollRestoration" in history) {
      const prev = (history as any).scrollRestoration;
      (history as any).scrollRestoration = "manual";
      return () => ((history as any).scrollRestoration = prev);
    }
  }, []);

  // 1) cały czas zapisuj scroll bieżącej strony (KLUCZ: pathname+search)
  useEffect(() => {
    const key = location.pathname + location.search;
    const save = () => {
      positionsRef.current[key] = window.scrollY;
    };
    // zapis po każdym scrollu (ostatnia wartość zostanie)
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, [location.pathname, location.search]);

  // 2) po zmianie lokalizacji – PRZYWRÓĆ (nie nadpisuj niczego!)
  useLayoutEffect(() => {
    const key = location.pathname + location.search;
    const savedY = positionsRef.current[key];
    const restoredFromHistory =
      navType === "POP" && typeof savedY === "number" ? savedY : undefined;

    const resolveHashTarget = (): number | undefined => {
      if (!location.hash) return undefined;
      const rawId = location.hash.slice(1);
      if (!rawId) return 0;
      let id = rawId;
      try {
        id = decodeURIComponent(rawId);
      } catch {
        id = rawId;
      }
      const byId = document.getElementById(id);
      if (byId) {
        const rect = byId.getBoundingClientRect();
        return Math.max(0, window.scrollY + rect.top);
      }
      const named = document.getElementsByName(id)[0];
      if (named instanceof HTMLElement) {
        const rect = named.getBoundingClientRect();
        return Math.max(0, window.scrollY + rect.top);
      }
      return undefined;
    };

    // Tymczasowo wyłącz smooth + scroll-snap (często ściąga do góry)
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlSnap = html.style.scrollSnapType;
    const prevBodySnap = body.style.scrollSnapType;
    const prevHtmlBeh = html.style.scrollBehavior;

    html.style.scrollBehavior = "auto";
    html.style.scrollSnapType = "none";
    body.style.scrollSnapType = "none";

    let frames = 8; // kilka klatek, żeby wygrać z obrazkami/layoutem
    const tick = () => {
      const hashTarget =
        typeof restoredFromHistory === "number" ? undefined : resolveHashTarget();
      const targetY =
        typeof restoredFromHistory === "number"
          ? restoredFromHistory
          : typeof hashTarget === "number"
          ? hashTarget
          : 0;
      window.scrollTo(0, targetY);
      if (--frames > 0) requestAnimationFrame(tick);
      else {
        // przywróć style
        html.style.scrollBehavior = prevHtmlBeh;
        html.style.scrollSnapType = prevHtmlSnap;
        body.style.scrollSnapType = prevBodySnap;
      }
    };
    requestAnimationFrame(tick);

    // na wszelki wypadek zapisz stan do sessionStorage
    writeStore(positionsRef.current);
  }, [location.pathname, location.search, location.hash, navType]);

  // zapis na wyjściu
  useEffect(() => {
    const beforeUnload = () => writeStore(positionsRef.current);
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, []);

  return null;
}
